import type { AuthContextUser } from 'contexts';
import type { History } from 'history';
import { isAssumeOrgType, isFullAccessUser } from 'utils';
import type { ClaimPrefix } from './constants';
import {
  OrgStatus,
  ORG_STATUS_ORDER,
  StageName,
  STAGE_ORDER,
  SHOW_WELCOME_URL_PARAM,
} from './constants';
import type { Steps } from './types';
import type { GetOnboarding } from '../../types/getOnboardingType';

export const buildWizardUrl = ({
  prefix,
  urlParams,
  nextStep,
}: {
  prefix: ClaimPrefix;
  urlParams: UrlParams;
  nextStep: Steps;
}) => [
    urlParams.reduce(
      (acc, { name, value }) => acc.replace(`:${name}`, value),
      prefix,
    ),
    nextStep,
  ].join('/');

export type UrlParams = { name: string; value: string }[];

export type NavigationProps = {
  history: History;
  prefix: ClaimPrefix;
  urlParams: UrlParams;
  nextStep: Steps;
};

export const buildNavigationHandler =
  ({ history, prefix, urlParams, nextStep }: NavigationProps) =>
  () => {
    const next = buildWizardUrl({
      prefix,
      urlParams,
      nextStep,
    });

    if (history.location.search) {
      history.push(`${next}${history.location.search}`);
    } else {
      history.push(next);
    }
  };

/**
 * Returns
 * * `null` for no banner
 * * `'showingInstructions'` for configure instructions banner
 * * `'noAccess'` for limited access banner
 */
export const getDisplayedPreLaunchBanner = (
  auth: AuthContextUser | undefined,
  orgStatus: OrgStatus | null,
): null | 'showingInstructions' | 'noAccess' => {
  if (!auth || !orgStatus || isAssumeOrgType(auth.org.type)) return null;
  if (isFullAccessUser(auth)) return null;

  switch (orgStatus) {
    case OrgStatus.ACTIVE:
      return null;
    case OrgStatus.ONBOARDING:
      return 'showingInstructions';
    case OrgStatus.UNCLAIMED:
      return 'noAccess';
    default:
      throw new Error(`Unsupported orgStatus: ${orgStatus}`);
  }
};

export const shouldDisplayWizardBanner = (
  auth: AuthContextUser | undefined,
  onboarding: GetOnboarding['onboarding'] | undefined,
  orgStatus: OrgStatus | null,
) => {
  if (!auth || !orgStatus || isAssumeOrgType(auth.org.type)) return false;

  const isWizardStage =
    ORG_STATUS_ORDER[orgStatus] < ORG_STATUS_ORDER[OrgStatus.ONBOARDING] ||
    (onboarding?.currentStage?.name &&
      STAGE_ORDER[onboarding.currentStage.name] <=
        STAGE_ORDER[StageName.WIZARD]);

  return auth?.roles[auth.role] >= auth?.roles?.GLOBAL_ADMIN && isWizardStage;
};

export const shouldDisplayAgentPlanUpgradeBanner = (
  auth: AuthContextUser | undefined,
) => {
  if (!auth) return false;
  return (
    auth?.roles[auth.role] === auth?.roles?.OWNER || auth?.isBrokerOfRecord
  );
};

// There is currently no timezone saved at the top level org object,
// only at org.addresses[0][0].timezone. We cannot reliably use the timezone from the default/main office, as this *could* get deleted
// Launch dates are stored at the start of day in local time (Usually EST) as an ISO string. We only need the date itself, however depending on
// the timezone of the user the date could be in a different day (i.e. MST/PST will be the previous day). Displaying the shifted UTC date fixes
// this issue.
export const shiftToUtc = (brokerageLaunchDate: string) => {
  const launchDate = new Date(brokerageLaunchDate);
  // JS Date shifts input to local timezone,
  // so 2021-06-18 startDate from server will appear as
  // 2021-06-17 (for our timezone)
  // so shift date Object to appear as utc using offset
  return new Date(
    launchDate.getTime() + launchDate.getTimezoneOffset() * 60000,
  );
};

export const buildSSORedirect = (ssoRedirect: string) => {
  const decodedRedirect = decodeURIComponent(ssoRedirect.replace(/\+/g, '%20'));
  const [url, search] = decodedRedirect.split('?');

  const params = new URLSearchParams(search);

  params.append(SHOW_WELCOME_URL_PARAM, 'true');

  return `${url}?${params.toString()}`;
};
